import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Actions from '../actions/Actions';
import styles from './Slide.module.scss';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import Spinner from '../../../../components/spinner/Spinner';
import Button from '../../../../components/button/Button';
import { getMediaAbsolutePath } from '../../../../services/File';

type Props = {
  data: string;
  isFullScreen: boolean;
};

function Slide(props: Props) {
  const { data, isFullScreen } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    setPageNumber(1);
    setTotalPages(0);
  }, [data]);

  const fileObj = useMemo(() => {
    return getMediaAbsolutePath(data);
  }, [data]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setTotalPages(numPages);
  }, []);

  const calculateSize = useCallback(() => {
    if (wrapperRef.current) {
      const { width, height } = wrapperRef.current.getBoundingClientRect();
      setWidth(width);
      setHeight(height);
    }
  }, []);

  useLayoutEffect(() => {
    window.addEventListener('resize', calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener('resize', calculateSize);
    };
  }, [calculateSize]);

  useEffect(() => {
    setTimeout(calculateSize, 50);
  }, [calculateSize]);

  const prevDisabled = pageNumber <= 1;
  const isLast = pageNumber >= totalPages;

  const handleLeft = useCallback(() => {
    setPageNumber(Math.max(pageNumber - 1, 1));
  }, [pageNumber, setPageNumber]);

  const handleRight = useCallback(() => {
    setPageNumber(Math.min(pageNumber + 1, totalPages));
  }, [totalPages, pageNumber, setPageNumber]);

  const prevButton = useMemo(() => {
    if (prevDisabled) return null;
    return <Button size={'extraSmall'} onClick={handleLeft} label={'Previous'}/>;
  }, [prevDisabled, handleLeft]);

  const nextButton = useMemo(() => {
    if (isLast) return null;
    return <Button size={'extraSmall'} onClick={handleRight} label={'Next'}/>;
  }, [isLast, handleRight]);

  if (!fileObj) return null;

  return (
    <Actions fullScreen={isFullScreen} leftButton={prevButton} rightButton={nextButton} totalPages={totalPages} pageNumber={pageNumber}>
      <div className={`${styles.wrapper} ${isFullScreen ? styles.fullScreen : ''}`} ref={wrapperRef}>
        <div className={styles.content}>
          <Document
            file={fileObj}
            onLoadSuccess={onDocumentLoadSuccess}
            className={styles.document}
            loading={<Spinner/>}
          >
            <Page loading={<Spinner/>} pageNumber={pageNumber} width={width}
                  height={height}/>
          </Document>
        </div>
      </div>
    </Actions>
  );
}

export default Slide;
